import { useEffect, useMemo } from 'react';

import type { CheckboxButtonGroupOption } from '@capsule/consumer-design-system';
import { Button, SubmitButton, Typography } from '@capsule/consumer-design-system';

import { Formik } from 'formik';
import { useHistory } from 'react-router';
import { css } from 'styled-components';
import * as Yup from 'yup';

import CapsuleIcon from 'src/app/components/Elements/CapsuleIcon';
import { Divider } from 'src/app/components/Elements/Divider';
import Spinner from 'src/app/components/Elements/Spinner';
import { SimpleFeatureLayout } from 'src/app/components/FeatureLayouts/SimpleFeatureLayout/SimpleFeatureLayout';
import { FormikCheckboxTick } from 'src/app/components/Form/FormikCheckboxTick';
import { MedicationList } from 'src/app/components/PageSections/DoctorTransferForm/MedicationsFieldArray/MedicationList';
import type {
  HealthProfileMedicationTransferrable,
  InternalHealthProfileMedicationToVerify,
} from 'src/app/hooks/data/healthProfile';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { loggedInHealthProfile } from 'src/app/router/paths';
import { CAPSULE_ICON_STRINGS, FORM_VALIDATION_ERRORS } from 'src/consts';

import type { HealthProfileFormProps } from '..';
import { HealthProfileUpdateStepNames } from '../constants';
import { MedicationsCheckBoxPicker } from '../medicationsPicker';
import { NegativeMarginOffset } from '../styles';
import {
  formatMedicationWithStrengthAndLabel,
  formatStrengthAndLabel,
  getMedicationsTransferEligibility,
  replaceDotWithUnicode,
} from '../utils';

import { PillsBackground } from './PillsBackground';
import { MedicationBlockTitle, NonTransferrableMedicationsContainer } from './styles';
import { AnimatedBanner } from './SuccessBanner';

const FORM_ID = 'moveYourMedsForm';
const selectAll = 'selectAll';

const validationSchema = Yup.object().shape({
  form: Yup.array().test('form', FORM_VALIDATION_ERRORS.selectMedicationsPicker, function () {
    const options = Object.values(this.parent);
    return options.filter(value => !!value).length > 0;
  }),
});

export const MoveYourMeds = (props: HealthProfileFormProps & { createTransferTask: () => Promise<void> }) => {
  const {
    onToggleHideHeader,
    healthProfilePersistentData,
    setHealthProfilePersistentData,
    isMobile,
    createTransferTask,
  } = props;

  useEffect(() => {
    onToggleHideHeader(true);
  }, [onToggleHideHeader]);

  const currentStepName = HealthProfileUpdateStepNames.ConfirmMoveMedications;
  const history = useHistory();

  const { eligibleMeds, ineligibleMeds, capsuleMeds, doctorRequiredMeds } = useMemo(
    () => getMedicationsTransferEligibility(healthProfilePersistentData.selectedMeds),
    [healthProfilePersistentData.selectedMeds]
  );

  const totalMedsCount = useMemo(
    () => eligibleMeds.length + ineligibleMeds.length + capsuleMeds.length + doctorRequiredMeds.length,
    [eligibleMeds, ineligibleMeds, capsuleMeds, doctorRequiredMeds]
  );

  const dispatchSelectHealthProfileTransferMed = useSegmentEvent('selectHealthProfileTransferMed', {
    shouldTrackOnMount: false,
  });

  const dispatchDismissHealthProfileTransfer = useSegmentEvent('dismissHealthProfileTransfer', {
    shouldTrackOnMount: false,
  });

  const dispatchSubmitHealthProfileTransfer = useSegmentEvent('submitHealthProfileTransfer', {
    shouldTrackOnMount: false,
  });

  const { medicationsFormOptions, medicationsFormValues } = useMemo(
    () =>
      eligibleMeds.reduce(
        (acc, medication) => {
          acc.medicationsFormOptions.push({
            // We replace the "." with unicode "․" because Formik uses it as a property accessor, which broke things once
            label: replaceDotWithUnicode(medication.primaryName),
            value: replaceDotWithUnicode(formatMedicationWithStrengthAndLabel(medication)),
            description: formatStrengthAndLabel(medication),
            onChange: e => {
              if (e.target.checked) {
                dispatchSelectHealthProfileTransferMed({
                  medication_name: medication.primaryName,
                  eligible:
                    medication.eligibility.eligibleForTransfer && !medication.eligibility.doctorRequiredForTransfer,
                  select_all: false,
                });
              }
            },
          });
          acc.medicationsFormValues[replaceDotWithUnicode(formatMedicationWithStrengthAndLabel(medication))] = false;

          return acc;
        },
        {
          medicationsFormOptions: [] as Pick<
            CheckboxButtonGroupOption,
            'label' | 'value' | 'description' | 'onChange'
          >[],
          medicationsFormValues: {} as Record<string, boolean>,
        }
      ),
    [eligibleMeds, dispatchSelectHealthProfileTransferMed]
  );

  return (
    <Formik
      onSubmit={async (values, formikHelpers) => {
        const { setSubmitting, resetForm } = formikHelpers;
        setSubmitting(true);
        try {
          onToggleHideHeader(false);
          const selectedMeds = eligibleMeds.filter(
            (med: HealthProfileMedicationTransferrable) =>
              !!values[replaceDotWithUnicode(formatMedicationWithStrengthAndLabel(med))]
          );

          healthProfilePersistentData.medsToTransfer = selectedMeds;
          await setHealthProfilePersistentData(healthProfilePersistentData);

          dispatchSubmitHealthProfileTransfer({
            num_eligible_medications_shown: eligibleMeds.length,
            perc_eligible_medications_selected: Math.round((selectedMeds.length / eligibleMeds.length) * 100),
            num_total_medications_shown: healthProfilePersistentData.selectedMeds.length,
          });

          await createTransferTask();
          history.push(loggedInHealthProfile.toPath({ stepName: HealthProfileUpdateStepNames.Confirmation }));
          resetForm();
        } catch (e) {
          console.error('Error submitting form', e);
        } finally {
          setSubmitting(false);
        }
      }}
      validationSchema={validationSchema}
      initialValues={medicationsFormValues}
      enableReinitialize
    >
      {formik => {
        const { errors, isSubmitting, values, touched, setFieldValue, submitCount } = formik;

        /*
         * If user has clicked "Select All" to select all medications, then
         * unselects one, we uncheck the "Select All box"
         */
        const hasAnyUncheckedMeds =
          Object.entries(values).filter(([key, value]) => key != selectAll && !value).length > 0;

        if (hasAnyUncheckedMeds && !!values.selectAll) {
          setFieldValue(selectAll, false);
        }

        if (errors?.form && submitCount > 0) {
          // `form` error detects whether anything is selected. If *nothing* is selected, we gotta map the error
          // out to the FormikCheckboxButtonGroup options so they display as errored. Also have to override the
          // "touched" property, otherwise only items that were selected and unselected will display in red.
          Object.assign(touched, ...medicationsFormOptions.map(medication => ({ [medication.label]: true })));
          Object.assign(errors, ...medicationsFormOptions.map(medication => ({ [medication.label]: true })));
        }

        return (
          <PillsBackground isMobile={isMobile} key={currentStepName}>
            <AnimatedBanner>
              <>
                <CapsuleIcon iconString={CAPSULE_ICON_STRINGS.check_circle} />
                <Typography
                  variant={'small'}
                  css={css`
                    margin-top: 0.2rem;
                  `}
                >
                  Health profile updated!
                </Typography>
              </>
            </AnimatedBanner>
            <SimpleFeatureLayout
              shouldDisplayCapsuleLogo
              shouldCapsuleLogoLinkToHome
              preTitleHeader=" " // empty string to keep the spacing but have no text in the pre-title header
              backButton={{ isRendered: false }}
              title={
                <Typography variant={{ base: 'displayXl', smallDown: 'heading2' }}>
                  95% of customers use Capsule for
                  {<em> all </em>}
                  their meds
                </Typography>
              }
              description={`Move these meds to Capsule in one click. We’ll take care of the rest.`}
              keyName={currentStepName}
              body={{
                content:
                  /*
                   * HACK: medication eligibility is resolving slightly after the page loads and mounts
                   * causing the analytics component to fire before we have the data loaded. Since this
                   * page requires the user to have at least one eligible medication to show, we will use
                   * that as a proxy to determine if eligibility has finished loading.
                   */
                  eligibleMeds.length == 0 ? (
                    <Spinner centered />
                  ) : (
                    <>
                      <NegativeMarginOffset />
                      <TrackViewHealthProfileTransfer
                        num_eligible_medications={eligibleMeds.length}
                        perc_eligible_medications={Math.round((eligibleMeds.length / totalMedsCount) * 100) || 0}
                        num_capsule_medications={capsuleMeds.length}
                        perc_capsule_medications={Math.round((capsuleMeds.length / totalMedsCount) * 100) || 0}
                        num_doctor_ineligible_medications={doctorRequiredMeds.length}
                        perc_doctor_ineligible_medications={
                          Math.round((doctorRequiredMeds.length / totalMedsCount) * 100) || 0
                        }
                        num_not_fillable_ineligible_medications={ineligibleMeds.length}
                        perc_not_fillable_ineligible_medications={
                          Math.round((ineligibleMeds.length / totalMedsCount) * 100) || 0
                        }
                      />
                      <FormikCheckboxTick
                        name={'selectAll'}
                        checked={!!values.selectAll}
                        defaultChecked={false}
                        label={{
                          content: (
                            <Typography
                              css={css`
                                text-align: right;
                              `}
                              variant="body"
                            >
                              Select all
                            </Typography>
                          ),
                          isHidden: false,
                        }}
                        onChange={e => {
                          dispatchSelectHealthProfileTransferMed({
                            medication_name: '',
                            eligible: '',
                            select_all: true,
                          });
                          Object.keys(values).map(key => {
                            setFieldValue(key, e.target.checked);
                          });
                        }}
                        /*
                         * row-reverse here is to make the checkbox appear on the right side of the label,
                         * and the right shift is to align 'Select all' checkbox with the other checkboxes.
                         *
                         * We have to account for the box shadow on the side of the checkbox, so we add 4px
                         * to s4, which is the box padding.
                         */
                        css={css`
                          right: calc(4px + ${({ theme }) => theme.space.s4});
                          flex-direction: row-reverse;
                          justify-content: flex-start;
                        `}
                      />
                      <MedicationsCheckBoxPicker
                        medicationsFormOptions={medicationsFormOptions}
                        formik={formik}
                        formId={FORM_ID}
                      />
                      <div
                        css={css`
                          display: ${errors.form && submitCount > 0 ? 'block' : 'none'};
                          color: ${({ theme }) => theme.colors.capsuleRed60};
                        `}
                      >
                        <Typography variant="small">Please select at least one medication.</Typography>
                      </div>

                      <NonTransferrableMedications
                        ineligibleMeds={ineligibleMeds}
                        doctorRequiredMeds={doctorRequiredMeds}
                        capsuleMeds={capsuleMeds}
                      />

                      <SubmitButton variant="contained-secondary-lg" isSubmitting={isSubmitting} form={FORM_ID}>
                        Move to Capsule
                      </SubmitButton>
                      <Button
                        css={css`
                          margin-bottom: ${({ theme }) => theme.space.s6};
                        `}
                        variant="text-md"
                        isFullWidth
                        onClick={() => {
                          dispatchDismissHealthProfileTransfer({});
                          history.push(
                            loggedInHealthProfile.toPath({ stepName: HealthProfileUpdateStepNames.JustSayCapsule })
                          );
                        }}
                      >
                        I'll use multiple pharmacies
                      </Button>
                    </>
                  ),
              }}
              error={errors?.form}
            />
          </PillsBackground>
        );
      }}
    </Formik>
  );
};

const NonTransferrableMedications = (props: {
  ineligibleMeds: HealthProfileMedicationTransferrable[];
  capsuleMeds: InternalHealthProfileMedicationToVerify[];
  doctorRequiredMeds: HealthProfileMedicationTransferrable[];
}) => {
  const { ineligibleMeds, capsuleMeds, doctorRequiredMeds } = props;
  const hasIneligibleMeds = ineligibleMeds?.length > 0;
  const hasCapsuleMeds = capsuleMeds?.length > 0;
  const hasDoctorRequiredMeds = doctorRequiredMeds?.length > 0;

  return (
    <NonTransferrableMedicationsContainer>
      {(hasCapsuleMeds || hasDoctorRequiredMeds || hasIneligibleMeds) && <Divider width={'100%'} />}

      {hasDoctorRequiredMeds && (
        <div>
          <MedicationBlockTitle variant="overline">Ask your doctor to send these meds</MedicationBlockTitle>
          <MedicationList
            medications={doctorRequiredMeds.map(med => formatMedicationWithStrengthAndLabel(med))}
            hideMedicationHeader
          />
          <Typography
            variant="small"
            css={css`
              margin-bottom: ${({ theme }) => theme.space.s2};
            `}
          >
            Your doctor needs to directly send us prescriptions for controlled substances{' '}
          </Typography>
        </div>
      )}
      {hasCapsuleMeds && (
        <div>
          <MedicationBlockTitle variant="overline">Meds filled by Capsule</MedicationBlockTitle>
          <MedicationList
            medications={capsuleMeds.map(med => formatMedicationWithStrengthAndLabel(med))}
            hideMedicationHeader
          />
        </div>
      )}
      {hasIneligibleMeds && (
        <div>
          <MedicationBlockTitle variant="overline">These meds are unavailable</MedicationBlockTitle>
          <MedicationList
            medications={ineligibleMeds.map(med => formatMedicationWithStrengthAndLabel(med))}
            hideMedicationHeader
          />
        </div>
      )}
    </NonTransferrableMedicationsContainer>
  );
};

interface TrackViewHealthProfileTransferPayload {
  num_eligible_medications: number;
  perc_eligible_medications: number;
  num_capsule_medications: number;
  perc_capsule_medications: number;
  num_doctor_ineligible_medications: number;
  perc_doctor_ineligible_medications: number;
  num_not_fillable_ineligible_medications: number;
  perc_not_fillable_ineligible_medications: number;
}

const TrackViewHealthProfileTransfer = (payload: TrackViewHealthProfileTransferPayload) => {
  useSegmentEvent('viewHealthProfileTransfer', { shouldTrackOnMount: true, payload });

  return null;
};
